import { getFilter } from 'data/filters';
import useActivitiesFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/useActivitiesFilter';
import useAmountOfSearchResults from 'modules/common/Popovers/MoreFiltersPopover/hooks/useAmountOfSearchResults';
import useDurationFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/useDurationFilter';
import useKeywordFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/useKeywordFilter';
import usePriceFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/usePriceFilter';
import useTopFilters from 'modules/common/Popovers/MoreFiltersPopover/hooks/useTopFilters';
import useToursFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/useToursFilter';
import useTransportModeFilter from 'modules/common/Popovers/MoreFiltersPopover/hooks/useTransportModeFilter';
import { PopoverTriggerSource } from 'modules/common/SubBars/ExperienceFilterSubNavbar/usePopoverTrigger';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import { useTrekker } from 'utils/analytics';
import useLocalizedNavigate from '~/hooks/useLocalizedNavigate';

export type ActiveFilters = {
  keywords: string[];
  topFilterOptions: string[];
  activitiesOptions: string[];
  activitiesFilterStatus: boolean;
  toursOptions: string[];
  toursFilterStatus: boolean;
  transportModeOptions: string[];
  priceOptions: string[];
  durationOptions: string[];
};

export type FilterActions = {
  onKeywordAdd: (keyword: string) => void;
  onKeywordRemove: (keyword: string) => void;
  onToggleTopFilter: (filter: string) => void;
  onToggleActivities: (activity: string) => void;
  onToggleAllActivities: () => void;
  onToggleTours: (tour: string) => void;
  onToggleAllTours: () => void;
  onToggleTransportMode: (mode: string) => void;
  onTogglePrice: (priceRange: string) => void;
  onToggleDuration: (durationRange: string) => void;
};

interface MoreFiltersReturnType {
  onApply: (source: PopoverTriggerSource) => (e: any) => void;
  onClear: () => void;
  results: {
    amountOfResults: number;
    resultsLoading: boolean;
  };
  activeFilters: ActiveFilters;
  actions: FilterActions;
}

const MORE_FILTER_UIDS = [
  'keywords',
  'activityType.activity',
  'price.items[]',
  'subcategory.items[]',
  'other.transport_mode.items[]',
  'duration.items[]',
  'family-friendly',
  'instantconfirmation',
];

function useMoreFilters({
  initialSelectedFilters,
  onClose,
  aggregations,
  city,
  country,
}): MoreFiltersReturnType {
  const trekker = useTrekker();

  const { keywords, onKeywordRemove, onKeywordAdd } = useKeywordFilter(
    initialSelectedFilters,
  );

  const { topFilterOptions, onToggleTopFilter } = useTopFilters(
    initialSelectedFilters,
  );

  const {
    activitiesOptions,
    activitiesFilterStatus,
    onToggleActivities,
    onToggleAllActivities,
  } = useActivitiesFilter(initialSelectedFilters, aggregations);

  const { toursOptions, toursFilterStatus, onToggleTours, onToggleAllTours } =
    useToursFilter(initialSelectedFilters, aggregations);

  const { transportModeOptions, onToggleTransportMode } =
    useTransportModeFilter(initialSelectedFilters);

  const { priceOptions, onTogglePrice } = usePriceFilter(
    initialSelectedFilters,
  );

  const { durationOptions, onToggleDuration } = useDurationFilter(
    initialSelectedFilters,
  );

  const { amountOfResults, resultsLoading } = useAmountOfSearchResults({
    keywords,
    activitiesFilterStatus,
    toursFilterStatus,
    activitiesOptions,
    topFilterOptions,
    durationOptions,
    toursOptions,
    transportModeOptions,
    priceOptions,
    otherFilters: initialSelectedFilters.filter(
      (filter) => !MORE_FILTER_UIDS.includes(filter.uid),
    ),
    city,
    country,
  });

  const createFiltersToAdd = () => {
    const filtersToAdd = initialSelectedFilters.filter(
      (filter) => !MORE_FILTER_UIDS.includes(filter.uid),
    );

    const addFilter = (filter, options) => {
      const newFilter = getFilter(filter);
      newFilter.subOptions = options.map((value) =>
        getFilter(filter).subOptions.find(
          (option) => option.value === value || option.queryValue === value,
        ),
      );
      filtersToAdd.push(newFilter);
    };

    filtersToAdd.push({
      ...getFilter('keywords'),
      value: keywords.join('+').trim().toLowerCase(),
    });

    addFilter('price.items[]', priceOptions);
    addFilter('duration.items[]', durationOptions);
    addFilter('other.transport_mode.items[]', transportModeOptions);

    filtersToAdd.push({
      ...getFilter('family-friendly'),
      value: topFilterOptions.includes('family-friendly') ? 'true' : undefined,
    });

    filtersToAdd.push({
      ...getFilter('instantconfirmation'),
      value: topFilterOptions.includes('instantconfirmation')
        ? 'true'
        : undefined,
    });

    filtersToAdd.push({
      ...getFilter('activityType.activity'),
      subOptions: getFilter('activityType.activity').subOptions.filter(
        (activityType) =>
          (activitiesFilterStatus && activityType.value === 'activities') ||
          (toursFilterStatus && activityType.value === 'tours'),
      ),
    });

    filtersToAdd.push({
      ...getFilter('subcategory.items[]'),
      subOptions: [...activitiesOptions, ...toursOptions].map((value) =>
        getFilter('subcategory.items[]').subOptions.find(
          (subcategory) =>
            subcategory.value === value || subcategory.queryValue === value,
        ),
      ),
    });

    return filtersToAdd;
  };

  const navigate = useLocalizedNavigate();

  const onApply = (source: PopoverTriggerSource) => () => {
    const filtersToAdd = createFiltersToAdd();
    const activityTypes = [];
    if (activitiesFilterStatus) {
      activityTypes.push('activities');
    }
    if (toursFilterStatus) {
      activityTypes.push('tours');
    }
    trekker.event('click', {
      buttonText: 'apply',
      action: 'more filters',
      keywords: keywords.join(', '),
      subCategories: [...activitiesOptions, ...toursOptions].join(', '),
      transportMode: transportModeOptions.join(', '),
      price: priceOptions.join(', '),
      durationFilter: durationOptions.join(', '),
      familyFriendly: topFilterOptions.includes('family-friendly')
        ? 'true'
        : undefined,
      instantConfirmation: topFilterOptions.includes('instantconfirmation')
        ? 'true'
        : undefined,
      categories: activityTypes.join(', '),
    });
    onClose({
      source,
      callback: () => {
        const { to, replace } = routeToSearchURL({
          filtersToAdd,
          replace: true,
        });
        navigate(to, { replace });
      },
    });
  };

  const onClear = () => {
    const filtersToRemove = [];
    initialSelectedFilters.forEach((category) => {
      if (MORE_FILTER_UIDS.includes(category.uid)) {
        const categoryFilter = getFilter(category.uid);
        categoryFilter.subOptions = [];
        categoryFilter.value = '';
        filtersToRemove.push(categoryFilter);
      }
    });

    trekker.event('click', {
      buttonText: 'clear',
      action: 'more filters',
    });
    if (!onClose) {
      const { to, replace } = routeToSearchURL({
        filtersToAdd: filtersToRemove,
        replace: true,
      });
      navigate(to, { replace });
    } else {
      onClose({
        source: 'clear button',
        callback: () => {
          const { to, replace } = routeToSearchURL({
            filtersToAdd: filtersToRemove,
            replace: true,
          });
          navigate(to, { replace });
        },
      });
    }
  };

  return {
    onClear,
    onApply,
    results: {
      amountOfResults,
      resultsLoading,
    },
    activeFilters: {
      keywords,
      topFilterOptions,
      activitiesOptions,
      activitiesFilterStatus,
      toursOptions,
      toursFilterStatus,
      transportModeOptions,
      priceOptions,
      durationOptions,
    },
    actions: {
      onKeywordAdd,
      onKeywordRemove,
      onToggleTopFilter,
      onToggleActivities,
      onToggleAllActivities,
      onToggleTours,
      onToggleAllTours,
      onToggleTransportMode,
      onTogglePrice,
      onToggleDuration,
    },
  };
}

export default useMoreFilters;
